import { dedent } from 'ts-dedent';
import { ColumnDataType, Language, Provider, TokenAllowedApis, WorkflowCodeType, WorkflowComponentParameterMode, WorkflowComponentParamType } from '../types';
import { isExpressionValue } from '../utils';
import { SINGLE_OUTPUT_NAME, SingleOutputWorkflowComponent } from './common/singleoutputcomponent';
class CreateBuilderMap extends SingleOutputWorkflowComponent {
    constructor() {
        super({
            name: 'native.createbuildermap',
            group: 'Input/Output',
            title: 'Create Builder Map',
            description: `
          This component creates a Builder map on its first execution. On subsequent executions, it can update, overwrite or create a copy of the existing map.
        `,
            starred: true,
            warningTitle: 'Create copy mode',
            warningDescription: 'Please note that automating this workflow via API or scheduling it may lead to the creation of a large number of maps.',
            warningShowIf: [{ parameter: 'mode', value: 'copy' }]
        }, [
            {
                name: 'sources',
                type: WorkflowComponentParamType.InputTable,
                title: 'Source tables',
                mode: WorkflowComponentParameterMode.Multiple
            },
            {
                name: 'mapName',
                type: WorkflowComponentParamType.String,
                title: 'Map name',
                placeholder: 'workflows.parameterForm.createBuilderMapPlaceholder'
            },
            {
                name: 'mode',
                type: WorkflowComponentParamType.Selection,
                mode: WorkflowComponentParameterMode.Radio,
                title: 'Re-running mode',
                helper: 'This setting controls the behavior after the first execution',
                options: [
                    {
                        value: 'update',
                        name: 'Update',
                        description: 'Updates data sources while keeping map styles, widgets and other settings.',
                        icon: 'update'
                    },
                    {
                        value: 'overwrite',
                        name: 'Overwrite',
                        description: 'Deletes the current map and creates a new one with default settings.',
                        icon: 'autorenew'
                    },
                    {
                        value: 'copy',
                        name: 'Create copy',
                        description: 'Creates a new copy of the map with same styles and other settings.',
                        icon: 'mapcopy'
                    }
                ],
                value: 'update',
                default: 'update',
                allowExpressions: false
            }
        ]);
        this.supportedProviders = [
            Provider.BigQuery,
            Provider.Snowflake,
            Provider.Redshift,
            Provider.DatabricksRest
        ];
    }
    /**
     * Generates a SQL command to the __CREATE_BUILDER_MAP prodecure for the selected
     * provider.
     * This function supports both dry run and actual execution modes.
     *
     * @param {WorkflowComponentSQLParams} params - The parameters needed to generate the SQL command.
     * @returns {Promise<QueryAndOutputs>} A promise that resolves to the SQL query and output settings.
     */
    async getCode({ workflow, params, connection, envVars, componentName, codeType }) {
        const isDryRun = codeType === WorkflowCodeType.DryRun;
        const isStoredProcedure = codeType === WorkflowCodeType.StoredProcedure;
        const preparedParams = this._prepareParameters(params);
        const tablename = connection.getOutputTableName(workflow.id, this.metadata.name, params, SINGLE_OUTPUT_NAME, codeType);
        const quotedTablename = connection.quoteName(tablename);
        let query = '';
        if (!isDryRun) {
            const analyticsToolbox = connection.quoteName(envVars.analyticsToolboxDataset);
            const preparedSources = this._prepareSources(preparedParams, workflow, componentName);
            const datasets = preparedSources.map((preparedSource) => ({
                type: 'table',
                source: preparedSource.source,
                sourceWorkflowNodeId: preparedSource.sourceWorkflowNodeId
            }));
            const mapNameParam = workflow.componentValues[componentName]
                ?.mapName?.value;
            let mapName;
            if (isExpressionValue(mapNameParam)) {
                mapName = preparedParams.mapName;
            }
            else {
                mapName = `'${preparedParams.mapName}'`;
            }
            const dropTable = isStoredProcedure
                ? ''
                : `DROP TABLE IF EXISTS ${quotedTablename};`;
            switch (connection.type) {
                case Provider.BigQuery:
                    query = dedent `
            ${dropTable}
            CALL ${analyticsToolbox}.__CREATE_BUILDER_MAP(
              '${envVars.apiBaseUrl}',
              '${envVars.accessToken}',
              '${connection.name}',
              ${mapName},
              '${preparedParams.mode}',
              '${workflow.id}',
              '${componentName}',
              '${JSON.stringify(datasets)}',
              '${envVars.domain}',
              '${tablename}',
              NULL);
          `;
                    break;
                case Provider.Snowflake:
                    query = dedent `
            ${dropTable}
            CALL ${analyticsToolbox}._CREATE_BUILDER_MAP(
              '${connection.name}',
              ${mapName},
              '${preparedParams.mode}',
              '${workflow.id}',
              '${componentName}',
              '${JSON.stringify(datasets)}',
              '${envVars.domain}',
              '${tablename}',
              NULL,
              '${envVars.apiBaseUrl}',
              '${envVars.accessToken}'
            );
          `;
                    break;
                case Provider.Redshift:
                    query = dedent `
            COMMIT;
            ${dropTable}
            CALL ${analyticsToolbox}.__CREATE_BUILDER_MAP(
              '${connection.name}',
              ${mapName},
              '${preparedParams.mode}',
              '${workflow.id}',
              '${componentName}',
              '${JSON.stringify(datasets)}',
              '${envVars.domain}',
              '${tablename}',
              NULL,
              '${envVars.apiBaseUrl}',
              '${envVars.accessToken}'
            );
          `;
                    break;
                case Provider.DatabricksRest:
                    const dropTableQuery = dedent `
            DROP TABLE IF EXISTS ${quotedTablename};
          `;
                    query = dedent `
            ${connection.wrapSQLCodeAsPythonCall(dropTableQuery)}

            import requests

            # Set up headers
            headers = {
                "Authorization": f"Bearer ${envVars.accessToken}",
                "Content-Type": "application/json"
            }

            # Define endpoints
            resources_endpoint = f"${envVars.apiBaseUrl}/v3/connections/${connection.name}/resources"
            map_endpoint = f"${envVars.apiBaseUrl}/v3/workflows/${workflow.id}/map"

            # Get enriched datasets
            enriched_datasets = []
            for dataset in ${JSON.stringify(datasets)}:
                try:
                    dataset_resource_endpoint = f"{resources_endpoint}/{dataset['source']}"
                    response = requests.get(dataset_resource_endpoint, headers=headers)
                    result = response.json()
                    if 'geomField' in result:
                        dataset['geoColumn'] = result['geomField']
                    enriched_datasets.append(dataset)
                except:
                    enriched_datasets.append(dataset)

            # Prepare the payload
            payload = {
                "mapName": ${mapName.replace(/\((.*?)\)/, '$1')},
                "mode": "${preparedParams.mode}",
                "workflowNodeId": "${componentName}",
                "datasets": enriched_datasets
            }

            try:
                # Make the request to create map
                response = requests.post(map_endpoint, json=payload, headers=headers)
                response.raise_for_status()
                result = response.json()

                # Create output table with results
                if "mapId" in result and "updatedAt" in result:
                    map_id = result["mapId"]
                    map_url = f"https://${envVars.domain}/builder/{map_id}"
                    updated_at = result["updatedAt"]

                    spark.sql(f"""
                        CREATE OR REPLACE TABLE ${quotedTablename} AS
                        SELECT
                            '{map_id}' as map_id,
                            '{map_url}' as map_url,
                            TIMESTAMP('{updated_at}') as updated_at
                    """)
                else:
                    raise RuntimeError(f"Invalid API response format. Response: {result}")
            except requests.exceptions.HTTPError as e:
                raise RuntimeError(f"API request failed: {str(e)} - Response: {e.response.text}")
            except requests.exceptions.RequestException as e:
                raise RuntimeError(f"Request failed: {str(e)}")
            except Exception as e:
                raise RuntimeError(f"Error creating builder map: {str(e)}")
            `;
                    break;
                default:
                    return null;
            }
        }
        return {
            outputs: {
                [SINGLE_OUTPUT_NAME]: tablename
            },
            query
        };
    }
    language(provider) {
        if (provider === Provider.DatabricksRest) {
            return Language.Python;
        }
        return Language.SQL;
    }
    getRequiredGrants({ workflow, connection, params, componentName }) {
        const preparedParams = this._prepareParameters(params);
        const preparedSources = this._prepareSources(preparedParams, workflow, componentName);
        const grants = preparedSources.map((preparedSource) => ({
            connection_name: connection.name,
            resource: preparedSource.source
        }));
        return {
            grants,
            apis: [TokenAllowedApis.workflows, TokenAllowedApis.resources]
        };
    }
    canProvideOutputSchemas() {
        return true;
    }
    getOutputSchema() {
        return [
            { name: 'map_id', type: ColumnDataType.String },
            { name: 'map_url', type: ColumnDataType.String },
            { name: 'updated_at', type: ColumnDataType.Timestamp }
        ];
    }
    requiredAnalyticsToolboxVersion(provider) {
        switch (provider) {
            case Provider.BigQuery:
                return '1.21.0';
            case Provider.Snowflake:
                return '1.19.0';
            case Provider.Redshift:
                return '1.10.0';
        }
        return null;
    }
    getVersion(provider) {
        return '1';
    }
    _prepareSources(preparedParams, workflow, componentName) {
        const inputSources = workflow.componentValues[componentName]
            .sources;
        return preparedParams.sources.map((source, index) => {
            const inputSourceName = (inputSources?.[index]?.value).component;
            return {
                source,
                sourceWorkflowNodeId: inputSourceName
            };
        });
    }
}
export { CreateBuilderMap };
